import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, updateDoc, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import Toast from '../components/Toast';

const DEFAULT_PROFILE_IMAGE = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png';

// Funciones utilitarias responsivas simplificadas
const isMobile = () => window.innerWidth <= 768;
const isTablet = () => window.innerWidth > 768 && window.innerWidth <= 1024;

const getResponsiveSize = (mobile, desktop) => {
  return isMobile() ? mobile : desktop;
};

const getResponsivePadding = (mobile, desktop) => {
  return isMobile() ? mobile : desktop;
};

const getResponsiveGap = (mobile, desktop) => {
  return isMobile() ? mobile : desktop;
};

const getResponsiveColumns = (type = 'default') => {
  if (type === 'products') {
    return isMobile() ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))'; // Reducido de 200px a 180px
  }
  return isMobile() ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))';
};

function DashboardMesero() {
  const navigate = useNavigate();
  
  // Estados principales
  const [userProfile, setUserProfile] = useState({
    nombre: '',
    rol: '',
    imagenURL: DEFAULT_PROFILE_IMAGE
  });
  const [seccionActiva, setSeccionActiva] = useState('panel');
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  
  // Estados para productos y mesas
  const [productos, setProductos] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [categorias, setCategorias] = useState([]); // Categorías dinámicas desde Firestore
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('Todas');
  
  // Estados para órdenes
  const [ordenActual, setOrdenActual] = useState({
    productos: [],
    mesa: '',
    total: 0,
    notas: ''
  });
  const [ordenesEnProceso, setOrdenesEnProceso] = useState([]);
  const [ordenesParaCobrar, setOrdenesParaCobrar] = useState([]);
    // Estados para modales
  const [editandoOrden, setEditandoOrden] = useState(null);
  const [mostrarPanelOrden, setMostrarPanelOrden] = useState(false);

  // Estado para los mensajes Toast
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success'
  });

  // Función para cargar categorías desde Firestore
  const cargarCategorias = async () => {
    try {
      const categoriasSnapshot = await getDocs(collection(db, "categorias"));
      const categoriasDatos = categoriasSnapshot.docs.map(doc => ({
        id: doc.id,
        nombre: doc.data().nombre
      }));
      
      setCategorias(categoriasDatos);
    } catch (error) {
      console.error("Error al cargar categorías:", error);
      // En caso de error, usar categorías básicas de respaldo
      setCategorias([
        { id: 'bebidas', nombre: 'Bebidas' },
        { id: 'postres', nombre: 'Postres' },
        { id: 'helados', nombre: 'Helados' }
      ]);
    }
  };

  // Funciones utilitarias
  const formatearNumero = (numero) => {
    return new Intl.NumberFormat('es-CO').format(numero);
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = DEFAULT_PROFILE_IMAGE;
  };

  // Función para cerrar sesión
  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  // Función mejorada para mostrar mensajes
  const showToast = (message, type = 'success') => {
    setToast({
      visible: true,
      message,
      type
    });
  };

  // Función para cerrar el toast
  const closeToast = () => {
    setToast({ ...toast, visible: false });
  };

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
          navigate('/');
          return;
        }

        // Cargar perfil del mesero
        const meseroQuery = query(
          collection(db, "vendedores"), 
          where("username", "==", userData.username)
        );
        const meseroSnapshot = await getDocs(meseroQuery);
        
        if (!meseroSnapshot.empty) {
          const meseroData = meseroSnapshot.docs[0].data();
          const meseroProfile = {
            nombre: meseroData.username,
            rol: meseroData.rol,
            imagenURL: meseroData.imagenURL || DEFAULT_PROFILE_IMAGE,
            sucursal: meseroData.sucursal || 'No asignada'
          };
          
          console.log("👤 Perfil de mesero cargado:", meseroProfile);
          setUserProfile(meseroProfile);
        }

        // Cargar productos
        const productosSnapshot = await getDocs(collection(db, "productos"));
        const productosData = productosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          categoria: doc.data().categoria || 'Sin categoría' // Asegurar que todos los productos tengan categoría
        }));
        setProductos(productosData);

        // Cargar mesas (crear algunas por defecto si no existen)
        await cargarMesas();

      } catch (error) {
        console.error("Error al cargar datos:", error);
      }
    };

    cargarDatos();
  }, [navigate]);

  // useEffect para cargar categorías
  useEffect(() => {
    cargarCategorias();
  }, []);

  // useEffect separado para cargar órdenes cuando el perfil esté disponible
  useEffect(() => {
    if (userProfile.sucursal && userProfile.sucursal !== 'No asignada') {
      console.log("🔄 Cargando órdenes para sucursal:", userProfile.sucursal);
      cargarOrdenesEnProceso();
    }
  }, [userProfile.sucursal]);

  // useEffect separado para cargar mesas cuando el perfil esté disponible
  useEffect(() => {
    if (userProfile.sucursal && userProfile.sucursal !== 'No asignada') {
      console.log("🔄 Cargando mesas para sucursal:", userProfile.sucursal);
      cargarMesas();
    }
  }, [userProfile.sucursal]);

  // useEffect para manejar conflictos entre menú móvil y panel de orden
  useEffect(() => {
    if (isMobile() && mostrarPanelOrden && menuMovilAbierto) {
      setMenuMovilAbierto(false);
    }
  }, [mostrarPanelOrden, menuMovilAbierto]);

  // Función para cargar mesas de la sucursal del mesero
  const cargarMesas = async () => {
    try {
      if (!userProfile.sucursal || userProfile.sucursal === 'No asignada') {
        console.log("⚠️ No se puede cargar mesas: sucursal del mesero no válida");
        setMesas([]);
        return;
      }

      console.log("🔍 Cargando mesas para sucursal del mesero:", userProfile.sucursal);
      
      const mesasQuery = query(
        collection(db, "mesas"),
        where("sucursal", "==", userProfile.sucursal)
      );
      const mesasSnapshot = await getDocs(mesasQuery);
      
      const mesasDB = mesasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log("✅ Mesas cargadas:", mesasDB);
      setMesas(mesasDB);
    } catch (error) {
      console.error("Error al cargar mesas:", error);
      setMesas([]);
    }
  };

  const cargarOrdenesEnProceso = async () => {
    try {
      // Solo cargar órdenes si tenemos la sucursal del mesero
      if (!userProfile.sucursal || userProfile.sucursal === 'No asignada') {
        console.log("⚠️ No se puede cargar órdenes: sucursal del mesero no válida");
        setOrdenesEnProceso([]);
        setOrdenesParaCobrar([]);
        return;
      }

      console.log("🔍 Cargando órdenes para sucursal del mesero:", userProfile.sucursal);
      
      const ordenesQuery = query(
        collection(db, "ordenes"),
        where("estado", "in", ["en_proceso", "lista_para_cobrar"]),
        where("sucursal", "==", userProfile.sucursal) // Filtrar por sucursal del mesero
      );
      const ordenesSnapshot = await getDocs(ordenesQuery);
      const ordenesData = ordenesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("✅ Órdenes cargadas para sucursal:", ordenesData.length);
      
      setOrdenesEnProceso(ordenesData.filter(orden => orden.estado === 'en_proceso'));
      setOrdenesParaCobrar(ordenesData.filter(orden => orden.estado === 'lista_para_cobrar'));
    } catch (error) {
      console.error("Error al cargar órdenes:", error);
      // En caso de error de índice, cargar todas y filtrar en JavaScript
      if (error.message.includes('index')) {
        console.log("🔄 Intentando método alternativo sin índice...");
        try {
          const ordenesQuery = query(
            collection(db, "ordenes"),
            where("estado", "in", ["en_proceso", "lista_para_cobrar"])
          );
          const ordenesSnapshot = await getDocs(ordenesQuery);
          const todasOrdenes = ordenesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filtrar por sucursal en JavaScript
          const ordenesFiltradas = todasOrdenes.filter(orden => 
            orden.sucursal === userProfile.sucursal
          );
          
          setOrdenesEnProceso(ordenesFiltradas.filter(orden => orden.estado === 'en_proceso'));
          setOrdenesParaCobrar(ordenesFiltradas.filter(orden => orden.estado === 'lista_para_cobrar'));
        } catch (fallbackError) {
          console.error("Error en método alternativo:", fallbackError);
        }
      }
    }
  };
  // Funciones para manejo de órdenes
  const agregarProductoOrden = (producto) => {
    // Solo bloquear si el producto tiene stock definido y es 0
    if (producto.cantidad !== null && producto.cantidad <= 0) {
      showToast('Este producto no tiene stock disponible', 'warning');
      return;
    }

    const productoExistente = ordenActual.productos.find(p => p.id === producto.id);
    let nuevosProductos;
    
    if (productoExistente) {
      nuevosProductos = ordenActual.productos.map(p =>
        p.id === producto.id
          ? { ...p, cantidadOrden: p.cantidadOrden + 1 }
          : p
      );
    } else {
      nuevosProductos = [...ordenActual.productos, {
        ...producto,
        cantidadOrden: 1
      }];
    }

    const nuevoTotal = nuevosProductos.reduce(
      (sum, p) => sum + (p.precio * p.cantidadOrden), 0
    );

    setOrdenActual({
      ...ordenActual,
      productos: nuevosProductos,
      total: nuevoTotal
    });

    // En móvil, solo mostrar un toast de confirmación en lugar de abrir/cerrar el panel
    if (isMobile()) {
      const esNuevoProducto = !productoExistente;
      const mensaje = esNuevoProducto 
        ? `${producto.nombre} agregado al pedido`
        : `+1 ${producto.nombre}`;
      showToast(mensaje, 'success');
    }
  };

  const quitarProductoOrden = (productoId) => {
    const nuevosProductos = ordenActual.productos.filter(p => p.id !== productoId);
    const nuevoTotal = nuevosProductos.reduce(
      (sum, p) => sum + (p.precio * p.cantidadOrden), 0
    );

    setOrdenActual({
      ...ordenActual,
      productos: nuevosProductos,
      total: nuevoTotal
    });
  };

  // Función para limpiar toda la orden
  const limpiarOrden = () => {
    setOrdenActual({
      productos: [],
      mesa: '',
      total: 0,
      notas: ''
    });
    showToast('Orden limpiada', 'info');
  };
  const tomarOrden = async () => {
    try {
      if (!ordenActual.productos.length) {
        showToast('Por favor, agregue productos a la orden', 'warning');
        return;
      }

      if (!ordenActual.mesa) {
        showToast('Por favor, seleccione una mesa', 'warning');
        return;
      }

      // Si estamos editando una orden existente
      if (editandoOrden) {
        await guardarCambiosOrden();
        return;
      }

      // Crear nueva orden
      const ordenData = {
        productos: ordenActual.productos,
        mesa: ordenActual.mesa,
        total: ordenActual.total,
        notas: ordenActual.notas,
        mesero: userProfile.nombre,
        estado: 'en_proceso',
        fecha: Timestamp.now(),
        sucursal: userProfile.sucursal || 'No asignada' // Incluir sucursal del mesero
      };

      await addDoc(collection(db, "ordenes"), ordenData);

      // Actualizar estado de la mesa
      const mesaSeleccionada = mesas.find(m => m.numero.toString() === ordenActual.mesa);
      if (mesaSeleccionada) {
        await updateDoc(doc(db, "mesas", mesaSeleccionada.id), {
          estado: 'ocupada'
        });
      }

      // Limpiar orden actual
      setOrdenActual({
        productos: [],
        mesa: '',
        total: 0,
        notas: ''
      });

      // En móvil, cerrar el panel de orden
      if (isMobile()) {
        setMostrarPanelOrden(false);
      }

      // Recargar datos
      await cargarMesas();
      await cargarOrdenesEnProceso();
      
      showToast('¡Orden tomada exitosamente!', 'success');
    } catch (error) {
      console.error("Error al tomar orden:", error);
      showToast('Error al tomar la orden: ' + error.message, 'error');
    }
  };
  
  const marcarListaParaCobrar = async (ordenId) => {
    try {
      await updateDoc(doc(db, "ordenes", ordenId), {
        estado: 'lista_para_cobrar'
      });
      
      await cargarOrdenesEnProceso();
      showToast('Orden marcada como lista para cobrar', 'success');
    } catch (error) {
      console.error("Error al marcar orden:", error);
      showToast('Error al marcar la orden: ' + error.message, 'error');
    }
  };

  const editarOrden = (orden) => {
    console.log('=== INICIANDO EDICION DE ORDEN ===');
    console.log('Orden original recibida:', orden);
    console.log('Productos en orden original:', orden.productos);
    
    setEditandoOrden(orden);
    
    // Asegurar que todos los productos tengan las propiedades necesarias
    const productosParaEditar = orden.productos.map(p => {
      console.log('Procesando producto:', p);
      return {
        id: p.id,
        nombre: p.nombre,
        precio: p.precio,
        categoria: p.categoria,
        imagenURL: p.imagenURL,
        cantidad: p.cantidad, // stock disponible
        cantidadOrden: p.cantidadOrden || p.cantidad || 1 // cantidad en la orden
      };
    });
    
    console.log('Productos procesados para edición:', productosParaEditar);
    
    // Recalcular el total por si acaso
    const totalRecalculado = productosParaEditar.reduce(
      (sum, p) => sum + (p.precio * p.cantidadOrden), 0
    );
    
    const nuevaOrdenActual = {
      productos: productosParaEditar,
      mesa: orden.mesa.toString(),
      total: totalRecalculado,
      notas: orden.notas || ''
    };
    
    console.log('Nueva orden actual configurada:', nuevaOrdenActual);
    setOrdenActual(nuevaOrdenActual);
    
    console.log('=== FIN CONFIGURACION EDICION ===');
    
    // Siempre redireccionar a la sección de tomar orden al editar cualquier orden
    setSeccionActiva('tomar_orden');
    
    // En móvil, mostrar automáticamente el panel de orden y cerrar el menú
    if (isMobile()) {
      setMostrarPanelOrden(true);
      setMenuMovilAbierto(false); // Cerrar el menú móvil para evitar superposición
    }
  };

  const guardarCambiosOrden = async () => {
    try {
      if (!editandoOrden) return;

      const ordenActualizada = {
        productos: ordenActual.productos,
        total: ordenActual.total,
        notas: ordenActual.notas,
        fechaActualizacion: Timestamp.now()
      };

      // Si estamos editando una orden que estaba lista para cobrar, mantener su estado
      if (editandoOrden.estado === 'lista_para_cobrar') {
        ordenActualizada.estado = 'lista_para_cobrar';
      }

      await updateDoc(doc(db, "ordenes", editandoOrden.id), ordenActualizada);

      // Limpiar estado de edición
      setEditandoOrden(null);
      setOrdenActual({
        productos: [],
        mesa: '',
        total: 0,
        notas: ''
      });

      // En móvil, cerrar el panel de orden
      if (isMobile()) {
        setMostrarPanelOrden(false);
      }

      // Recargar órdenes
      await cargarOrdenesEnProceso();
      
      // Mostrar mensaje según el tipo de orden editada
      const mensaje = editandoOrden.estado === 'lista_para_cobrar' 
        ? 'Orden lista para cobrar actualizada exitosamente'
        : 'Orden actualizada exitosamente';
      
      showToast(mensaje, 'success');
    } catch (error) {
      console.error("Error al actualizar orden:", error);
      showToast('Error al actualizar la orden: ' + error.message, 'error');
    }
  };

  const cancelarEdicion = () => {
    setEditandoOrden(null);
    setOrdenActual({
      productos: [],
      mesa: '',
      total: 0,
      notas: ''
    });
    
    // En móvil, cerrar el panel de orden
    if (isMobile()) {
      setMostrarPanelOrden(false);
    }
  };

  const enviarACobrar = async (ordenId) => {
    try {
      const orden = ordenesParaCobrar.find(o => o.id === ordenId);
      if (!orden) return;

      // Crear venta para el vendedor
      const ventaData = {
        productos: orden.productos.map(p => ({
          ...p,
          cantidadVenta: p.cantidadOrden
        })),
        total: orden.total,
        mesa: orden.mesa,
        mesero: orden.mesero,
        estado: 'pendiente_cobro',
        fecha: Timestamp.now(),
        ordenId: ordenId,
        sucursal: userProfile.sucursal || 'No asignada'
      };

      await addDoc(collection(db, "ventas_pendientes"), ventaData);
      
      // Eliminar de órdenes para cobrar
      await deleteDoc(doc(db, "ordenes", ordenId));
      
      // Liberar mesa
      const mesaOrden = mesas.find(m => m.numero.toString() === orden.mesa);
      if (mesaOrden) {
        await updateDoc(doc(db, "mesas", mesaOrden.id), {
          estado: 'disponible'
        });
      }

      await cargarMesas();
      await cargarOrdenesEnProceso();
      
      showToast('Orden enviada al vendedor para cobro', 'success');
    } catch (error) {
      console.error("Error al enviar orden:", error);
      showToast('Error al enviar orden: ' + error.message, 'error');
    }
  };

  // Función para renderizar secciones
  const renderSeccion = () => {
    switch(seccionActiva) {
      case 'panel':
        return (
          <div 
            className={isMobile() ? 'mesero-content' : ''}
            style={{ 
              padding: getResponsiveSize('10px', '20px'),
              background: isMobile() ? 'linear-gradient(135deg, #FEFCF8 0%, #F5F5DC 100%)' : 'transparent'
            }}
          >
            <h2 
              className={isMobile() ? 'mesero-section-title' : ''}
              style={{ 
              color: '#8B4513',
              fontSize: getResponsiveSize('20px', '24px'),
              marginBottom: getResponsiveSize('15px', '20px'),
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📊 Panel Principal
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile() ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: getResponsiveSize('10px', '20px'),
              marginTop: getResponsiveSize('15px', '20px')
            }}>
              <div style={{
                padding: getResponsiveSize('15px', '20px'),
                background: '#ffffff',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #ddd',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ 
                  color: '#333',
                  fontSize: getResponsiveSize('14px', '18px'),
                  marginBottom: '10px'
                }}>
                  Órdenes en Proceso
                </h3>
                <p style={{ 
                  fontSize: getResponsiveSize('24px', '32px'), 
                  margin: '10px 0',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}>
                  {ordenesEnProceso.length}
                </p>
              </div>
              <div style={{
                padding: getResponsiveSize('15px', '20px'),
                background: '#ffffff',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #ddd',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ 
                  color: '#333',
                  fontSize: getResponsiveSize('14px', '18px'),
                  marginBottom: '10px'
                }}>
                  Listas para Cobrar
                </h3>
                <p style={{ 
                  fontSize: getResponsiveSize('24px', '32px'), 
                  margin: '10px 0',
                  color: '#28a745',
                  fontWeight: 'bold'
                }}>
                  {ordenesParaCobrar.length}
                </p>
              </div>
              <div style={{
                padding: getResponsiveSize('15px', '20px'),
                background: '#ffffff',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #ddd',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ 
                  color: '#333',
                  fontSize: getResponsiveSize('14px', '18px'),
                  marginBottom: '10px'
                }}>
                  Mesas Libres
                </h3>
                <p style={{ 
                  fontSize: getResponsiveSize('24px', '32px'), 
                  margin: '10px 0',
                  color: '#6c757d',
                  fontWeight: 'bold'
                }}>
                  {mesas.filter(m => m.estado === 'libre' || m.estado === 'disponible').length}
                </p>
              </div>
            </div>
          </div>
        );

      case 'tomar_orden':
        // Filtrar productos por categoría y búsqueda
        const productosFiltrados = productos.filter(producto => {
          const coincideBusqueda = producto.nombre.toLowerCase().includes(busqueda.toLowerCase());
          const coincideCategoria = filtroCategoria === 'Todas' || producto.categoria === filtroCategoria;
          return coincideBusqueda && coincideCategoria;
        });

        // Agrupar productos por categoría si no hay filtro específico
        const productosPorCategoria = {};
        if (filtroCategoria === 'Todas') {
          categorias.forEach(cat => {
            productosPorCategoria[cat.nombre] = productosFiltrados.filter(
              p => p.categoria === cat.nombre
            );
          });
          
          // Añadir productos sin categoría al final
          const sinCategoria = productosFiltrados.filter(
            p => !categorias.some(cat => cat.nombre === p.categoria)
          );
          if (sinCategoria.length > 0) {
            productosPorCategoria['Sin categoría'] = sinCategoria;
          }
        }

        return (
          <div 
            className={isMobile() ? 'mesero-content' : ''}
            style={{ 
              padding: isMobile() ? '5px' : '10px',
              display: 'flex', 
              flexDirection: isMobile() ? 'column' : 'row',
              gap: isMobile() ? '10px' : '15px',
              paddingBottom: isMobile() ? '120px' : '0',  // Espacio para el botón flotante en móvil
              minHeight: isMobile() ? 'calc(100vh - 100px)' : 'auto', // Altura mínima para permitir scroll
              overflowY: isMobile() ? 'visible' : 'auto' // Permitir scroll en móvil
            }}
          >
            {/* Lista de productos */}
            <div style={{ 
              flex: isMobile() ? 'none' : 2,
              minHeight: isMobile() ? '400px' : 'auto', // Altura mínima en móvil
              overflowY: isMobile() ? 'visible' : 'auto' // Permitir scroll natural
            }}>
              <div style={{ 
                marginBottom: '20px',
                display: 'flex', 
                flexDirection: isMobile() ? 'column' : 'row',
                gap: '10px' 
              }}>
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  style={{
                    flex: isMobile() ? 'none' : 1,
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '16px'
                  }}
                />
                
                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  style={{
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <option value="Todas">Todas las categorías</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nombre}>{cat.nombre}</option>
                  ))}
                </select>
              </div>
              
              <h2 style={{ 
                color: '#8B4513',
                fontSize: getResponsiveSize('18px', '22px'),
                marginBottom: getResponsiveSize('15px', '20px'),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderBottom: '3px solid #CD853F',
                paddingBottom: '8px'
              }}>
                🍽️ Menú
              </h2>
              
              {filtroCategoria !== 'Todas' ? (
                // Vista de productos filtrados por una categoría específica
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: getResponsiveColumns('products'),
                  gap: getResponsiveSize('8px', '12px'), // Reducido de 15px a 12px
                  padding: getResponsiveSize('5px', '0')
                }}>
                  {productosFiltrados.map(producto => (
                    <ProductoCard 
                      key={producto.id} 
                      producto={producto} 
                      agregarProductoOrden={agregarProductoOrden}
                      formatearNumero={formatearNumero}
                      isMobile={isMobile}
                      ordenActual={ordenActual}
                    />
                  ))}
                </div>
              ) : (
                // Vista agrupada por categorías
                Object.keys(productosPorCategoria).map(categoria => 
                  productosPorCategoria[categoria].length > 0 && (
                    <div key={categoria} style={{ marginBottom: '30px' }}>
                      <h3 style={{ 
                        borderBottom: '2px solid #CD853F', 
                        paddingBottom: '8px',
                        marginBottom: '15px',
                        color: '#8B4513',
                        fontSize: getResponsiveSize('16px', '20px'),
                        fontWeight: 'bold'
                      }}>
                        {categoria}
                      </h3>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: getResponsiveColumns('products'),
                        gap: getResponsiveSize('8px', '12px'), // Reducido de 15px a 12px
                        padding: getResponsiveSize('5px', '0')
                      }}>
                        {productosPorCategoria[categoria].map(producto => (
                          <ProductoCard 
                            key={producto.id} 
                            producto={producto} 
                            agregarProductoOrden={agregarProductoOrden}
                            formatearNumero={formatearNumero}
                            isMobile={isMobile}
                            ordenActual={ordenActual}
                          />
                        ))}
                      </div>
                    </div>
                  )
                )
              )}
            </div>

            {/* Panel de orden actual - Condicional en móvil */}
            {(!isMobile() || (isMobile() && mostrarPanelOrden)) && (
              <div style={{ 
                width: '100%',
                maxWidth: isMobile() ? '100%' : '400px',
                height: isMobile() ? 'auto' : 'calc(100vh - 80px)',
                position: isMobile() ? 'fixed' : 'sticky',
                top: isMobile() ? 'auto' : '20px',
                bottom: isMobile() ? '0' : 'auto',
                left: isMobile() ? '0' : 'auto',
                background: isMobile() ? 
                  'linear-gradient(to top, #FEFCF8 0%, #F5F5DC 100%)' : 
                  'linear-gradient(135deg, #FEFCF8 0%, #F8F8FF 100%)',
                padding: getResponsiveSize('10px', '15px'),
                boxShadow: isMobile() ? 
                  '0 -4px 20px rgba(139, 69, 19, 0.2)' : 
                  '0 4px 12px rgba(139, 69, 19, 0.1)',
                zIndex: 1001, // Aumentado para estar por encima del menú móvil
                display: 'flex',
                flexDirection: 'column',
                borderRadius: isMobile() ? '15px 15px 0 0' : '12px',
                maxHeight: isMobile() ? '85vh' : 'auto', // Aumentado de 70vh a 85vh para más espacio
                overflow: isMobile() ? 'auto' : 'visible', // Cambiar a auto para permitir scroll
                border: isMobile() ? '2px solid #CD853F' : '1px solid #D2B48C',
                // Optimización para scroll en móvil
                WebkitOverflowScrolling: 'touch'
              }}>
                {/* Botón para cerrar en móvil */}
                {isMobile() && (
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '5px' 
                  }}>
                    {/* Botón para limpiar orden */}
                    {ordenActual.productos.length > 0 && (
                      <button
                        onClick={limpiarOrden}
                        style={{
                          background: 'none',
                          border: '2px solid #dc3545',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: '#dc3545',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#A0522D';
                        }}
                      >
                        🗑️ Limpiar
                      </button>
                    )}
                    
                    {/* Botón para cerrar */}
                    <button
                      onClick={() => setMostrarPanelOrden(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#A0522D',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#8B4513'}
                      onMouseLeave={(e) => e.target.style.color = '#A0522D'}
                    >
                      ✖
                    </button>
                  </div>
                )}
                
                <h3 style={{ 
                  margin: '0 0 15px 0',
                  fontSize: getResponsiveSize('16px', '20px'),
                  textAlign: 'center',
                  color: editandoOrden ? '#A0522D' : '#8B4513',
                  fontWeight: 'bold'
                }}>
                  {editandoOrden ? '✏️ Editando Orden' : '🍽️ Orden Actual'}
                  {editandoOrden && (
                    <div style={{ 
                      fontSize: '12px', 
                      fontWeight: 'normal', 
                      marginTop: '5px',
                      color: '#CD853F'
                    }}>
                      Mesa {editandoOrden.mesa} - {ordenActual.productos.length} producto(s)
                    </div>
                  )}
                  {!editandoOrden && ordenActual.productos.length > 0 && (
                    <div style={{ 
                      fontSize: '12px', 
                      fontWeight: 'normal', 
                      marginTop: '5px',
                      color: '#CD853F'
                    }}>
                      {ordenActual.productos.length} producto(s)
                    </div>
                  )}
                </h3>
                
                {/* Selección de mesa */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Mesa:</label>                <select
                    value={ordenActual.mesa}
                    onChange={(e) => setOrdenActual({ ...ordenActual, mesa: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  >                  <option value="">Seleccionar mesa</option>
                  {mesas
                    .filter(mesa => mesa.estado === 'libre' || mesa.estado === 'disponible')
                    .sort((a, b) => a.numero - b.numero)
                    .map(mesa => (
                    <option key={mesa.id} value={mesa.numero}>
                      Mesa {mesa.numero} ({mesa.capacidad} personas)
                    </option>
                  ))}
                </select>
              </div>

              {/* Lista de productos de la orden */}
              <div style={{ 
                flex: 1,
                overflowY: 'auto',
                marginBottom: '15px',
                maxHeight: isMobile() ? '45vh' : 'calc(100vh - 400px)', // Reducido para dejar espacio a otros elementos
                minHeight: isMobile() ? '200px' : '150px', // Altura mínima garantizada
                padding: isMobile() ? '8px' : '5px',
                border: isMobile() ? '1px solid #dee2e6' : 'none',
                borderRadius: isMobile() ? '8px' : '0',
                backgroundColor: isMobile() ? '#fafafa' : 'transparent',
                // Optimización para scroll en móvil - MUY IMPORTANTE
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                scrollbarColor: '#ccc #f8f8f8',
                // Asegurar que el scroll funcione en touch devices
                touchAction: 'pan-y'
              }}>
                {/* Indicador de edición */}
                {editandoOrden && (
                  <div style={{
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #007bff',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginBottom: '15px',
                    fontSize: '13px',
                    color: '#0056b3'
                  }}>
                    📝 Editando orden de Mesa {editandoOrden.mesa} - {ordenActual.productos.length} producto(s)
                  </div>
                )}
                
                {/* Lista de productos */}
                {!ordenActual.productos || ordenActual.productos.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#666',
                    fontSize: '14px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px'
                  }}>
                    {editandoOrden ? 
                      '⚠️ Esta orden no tiene productos visibles. Agrega productos del menú.' : 
                      'No hay productos en la orden'
                    }
                  </div>
                ) : (
                  <div>
                    {ordenActual.productos.map((producto, index) => {
                      // Verificar que el producto tenga datos válidos
                      if (!producto || !producto.nombre) {
                        return (
                          <div key={index} style={{
                            padding: '10px',
                            backgroundColor: '#ffe6e6',
                            border: '1px solid #ff9999',
                            borderRadius: '4px',
                            marginBottom: '5px'
                          }}>
                            ❌ Producto inválido en posición {index}
                          </div>
                        );
                      }
                      
                      return (
                        <div key={producto.id || index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: isMobile() ? '8px' : '12px',
                          marginBottom: isMobile() ? '8px' : '5px',
                          backgroundColor: editandoOrden ? '#f8f9fa' : 'white',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          minHeight: isMobile() ? '60px' : 'auto'
                        }}>
                          <div style={{ flex: 1, marginRight: '10px' }}>
                            <p style={{ 
                              margin: '0 0 2px 0', 
                              fontWeight: 'bold', 
                              fontSize: isMobile() ? '13px' : '14px', 
                              color: '#333',
                              lineHeight: '1.3'
                            }}>
                              {producto.nombre || 'Sin nombre'}
                            </p>
                            <p style={{ 
                              margin: '0', 
                              fontSize: isMobile() ? '11px' : '13px', 
                              color: '#666',
                              lineHeight: '1.2'
                            }}>
                              ${formatearNumero(producto.precio || 0)} x {producto.cantidadOrden || 0}
                            </p>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: isMobile() ? '8px' : '10px',
                            flexShrink: 0
                          }}>
                            <span style={{ 
                              fontWeight: 'bold',
                              color: '#28a745',
                              fontSize: isMobile() ? '12px' : '14px',
                              minWidth: isMobile() ? '50px' : '60px',
                              textAlign: 'right'
                            }}>
                              ${formatearNumero((producto.precio || 0) * (producto.cantidadOrden || 0))}
                            </span>
                            <button
                              onClick={() => {
                                quitarProductoOrden(producto.id);
                              }}
                              style={{
                                padding: isMobile() ? '6px' : '4px 8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: isMobile() ? '10px' : '12px',
                                minWidth: isMobile() ? '32px' : 'auto',
                                height: isMobile() ? '32px' : 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ✖
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {ordenActual.productos.length > 0 && (
                <div style={{ 
                  borderTop: '1px solid #eee',
                  paddingTop: '15px',
                  backgroundColor: 'white',
                  marginTop: 'auto', // Empujar hacia abajo
                  flexShrink: 0, // No permitir que se comprima
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  {/* Notas */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Notas:</label>
                    <textarea
                      value={ordenActual.notas}
                      onChange={(e) => setOrdenActual({ ...ordenActual, notas: e.target.value })}
                      placeholder="Observaciones especiales..."
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        minHeight: '60px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    padding: '12px',
                    backgroundColor: editandoOrden ? '#e8f4fd' : '#f8f9fa',
                    borderRadius: '8px',
                    border: editandoOrden ? '2px solid #007bff' : '1px solid #dee2e6'
                  }}>
                    <h4 style={{ margin: '0', fontSize: '16px', fontWeight: 'bold' }}>
                      Total: ${formatearNumero(ordenActual.total)}
                    </h4>
                    {editandoOrden && (
                      <span style={{
                        fontSize: '12px',
                        color: '#0056b3',
                        fontWeight: '600',
                        padding: '4px 8px',
                        backgroundColor: 'white',
                        borderRadius: '12px'
                      }}>
                        📝 Editando
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexDirection: isMobile() ? 'column' : 'row', width: '100%' }}>
                    {editandoOrden && (
                      <button
                        onClick={cancelarEdicion}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: '#6c757d',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.2s ease',
                          width: '100%',
                          boxSizing: 'border-box',
                          minWidth: 0
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                      >
                        ❌ Cancelar
                      </button>
                    )}
                    <button
                      onClick={tomarOrden}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: editandoOrden ? '#CD853F' : '#8B4513',
                        color: '#FEFCF8',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        transition: 'background-color 0.2s ease',
                        width: '100%',
                        boxSizing: 'border-box',
                        minWidth: 0
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = editandoOrden ? '#A0522D' : '#654321'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = editandoOrden ? '#CD853F' : '#8B4513'}
                    >
                      {editandoOrden ? '💾 Guardar Cambios' : '🍽️ Tomar Orden'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
            
            {/* Botón flotante mejorado para mostrar orden en móvil */}
            {isMobile() && !mostrarPanelOrden && ordenActual.productos.length > 0 && (
              <div
                onClick={() => {
                  setMostrarPanelOrden(true);
                  setMenuMovilAbierto(false); // Cerrar menú móvil
                }}
                style={{
                  position: 'fixed',
                  bottom: '20px',
                  right: '20px',
                  background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                  color: '#ffffff',
                  border: '2px solid #0056b3',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(0, 123, 255, 0.3)',
                  zIndex: 1002, // Aumentado para estar por encima del panel de orden
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 18px',
                  minWidth: '130px',
                  justifyContent: 'center',
                  animation: 'bounce 0.3s ease-out',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    🛒
                    <span>{ordenActual.productos.length}</span>
                  </div>
                  <div style={{
                    fontSize: '11px',
                    opacity: 0.9,
                    fontWeight: '500'
                  }}>
                    ${formatearNumero(ordenActual.total)}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'ordenes_proceso':
        return (
          <div 
            className={isMobile() ? 'mesero-content' : ''}
            style={{ 
              padding: getResponsiveSize('10px', '20px'),
              background: isMobile() ? 'linear-gradient(135deg, #FEFCF8 0%, #F5F5DC 100%)' : 'transparent'
            }}
          >
            <h2 
              className={isMobile() ? 'mesero-section-title' : ''}
              style={{ 
              color: '#8B4513',
              fontSize: getResponsiveSize('18px', '22px'),
              marginBottom: getResponsiveSize('15px', '20px'),
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '3px solid #CD853F',
              paddingBottom: '8px'
            }}>
              ⏳ Órdenes en Proceso
            </h2>
            {ordenesEnProceso.length === 0 ? (
              <p>No hay órdenes en proceso</p>
            ) : (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile() ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '15px',
                marginTop: '20px'
              }}>
                {ordenesEnProceso.map(orden => (
                  <div key={orden.id} style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '12px',
                    padding: '15px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Cabecera de la tarjeta */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '15px',
                      borderBottom: '1px solid #f0f0f0',
                      paddingBottom: '10px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        color: '#333'
                      }}>
                        <span style={{ 
                          backgroundColor: '#ffc107', 
                          color: 'black', 
                          width: '24px', 
                          height: '24px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {orden.mesa}
                        </span>
                        <h4 style={{ margin: 0 }}>Mesa {orden.mesa}</h4>
                      </div>
                      <div style={{ 
                        color: '#666',
                        fontSize: '13px', 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span>⏰</span>
                        <span>{orden.fecha?.seconds ? new Date(orden.fecha.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</span>
                      </div>
                    </div>
                    
                    {/* Lista de productos */}
                    <div style={{ 
                      flex: 1,
                      marginBottom: '15px'
                    }}>
                      {orden.productos.map((prod, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px 0',
                          borderBottom: index < orden.productos.length - 1 ? '1px dashed #eee' : 'none'
                        }}>
                          <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span style={{
                              backgroundColor: '#e9ecef',
                              color: '#495057',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {prod.cantidadOrden}
                            </span>
                            <span style={{ fontWeight: '500' }}>{prod.nombre}</span>
                          </div>
                          <span style={{ 
                            color: '#28a745',
                            fontWeight: '600'
                          }}>
                            ${formatearNumero(prod.precio * prod.cantidadOrden)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Notas */}
                    {orden.notas && (
                      <div style={{ 
                        marginBottom: '15px',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        fontSize: '14px',
                        borderLeft: '3px solid #6c757d'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          marginBottom: '5px',
                          color: '#495057',
                          fontWeight: '600'
                        }}>
                          <span>📝</span>
                          <span>Notas:</span>
                        </div>
                        <div style={{ paddingLeft: '20px', color: '#666' }}>
                          {orden.notas}
                        </div>
                      </div>
                    )}

                    {/* Footer con total y acciones */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #f0f0f0',
                      paddingTop: '15px',
                      flexWrap: 'wrap',
                      gap: '10px'
                    }}>
                      <div style={{ 
                        backgroundColor: '#e9ecef',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontWeight: 'bold'
                      }}>
                        Total: <span style={{ color: '#28a745' }}>${formatearNumero(orden.total)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => editarOrden(orden)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#ffc107',
                            color: '#000000',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: getResponsiveSize('12px', '14px'),
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#e0a800'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#ffc107'}
                        >
                          <span>✏️</span>
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => marcarListaParaCobrar(orden.id)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#007bff',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: getResponsiveSize('12px', '14px'),
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                        >
                          <span>✅</span>
                          <span>Lista para Cobrar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'ordenes_cobrar':
        return (
          <div 
            className={isMobile() ? 'mesero-content' : ''}
            style={{ 
              padding: getResponsiveSize('10px', '20px'),
              background: isMobile() ? 'linear-gradient(135deg, #FEFCF8 0%, #F5F5DC 100%)' : 'transparent'
            }}
          >
            <h2 
              className={isMobile() ? 'mesero-section-title' : ''}
              style={{ 
              color: '#8B4513',
              fontSize: getResponsiveSize('18px', '22px'),
              marginBottom: getResponsiveSize('15px', '20px'),
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '3px solid #CD853F',
              paddingBottom: '8px'
            }}>
              💰 Órdenes para Cobrar
            </h2>
            {ordenesParaCobrar.length === 0 ? (
              <p>No hay órdenes listas para cobrar</p>
            ) : (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile() ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '15px',
                marginTop: '20px'
              }}>
                {ordenesParaCobrar.map(orden => (
                  <div key={orden.id} style={{
                    border: '1px solid #28a745',
                    borderRadius: '12px',
                    padding: '15px',
                    backgroundColor: '#f8fff8',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Cabecera de la tarjeta */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '15px',
                      borderBottom: '1px solid #e6f5e6',
                      paddingBottom: '10px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        color: '#28a745'
                      }}>
                        <span style={{ 
                          backgroundColor: '#28a745', 
                          color: 'white', 
                          width: '24px', 
                          height: '24px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {orden.mesa}
                        </span>
                        <h4 style={{ margin: 0 }}>Mesa {orden.mesa}</h4>
                      </div>
                      <div style={{ 
                        color: '#666',
                        fontSize: '13px', 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span>⏰</span>
                        <span>{orden.fecha?.seconds ? new Date(orden.fecha.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</span>
                      </div>
                    </div>
                    
                    {/* Lista de productos */}
                    <div style={{ 
                      flex: 1,
                      marginBottom: '15px'
                    }}>
                      {orden.productos.map((prod, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px 0',
                          borderBottom: index < orden.productos.length - 1 ? '1px dashed #e6f5e6' : 'none'
                        }}>
                          <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span style={{
                              backgroundColor: '#e8f5e8',
                              color: '#28a745',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {prod.cantidadOrden}
                            </span>
                            <span style={{ fontWeight: '500' }}>{prod.nombre}</span>
                          </div>
                          <span style={{ 
                            color: '#28a745',
                            fontWeight: '600'
                          }}>
                            ${formatearNumero(prod.precio * prod.cantidadOrden)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Notas */}
                    {orden.notas && (
                      <div style={{ 
                        marginBottom: '15px',
                        padding: '10px',
                        backgroundColor: '#e8f5e8',
                        borderRadius: '8px',
                        fontSize: '14px',
                        borderLeft: '3px solid #28a745'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          marginBottom: '5px',
                          color: '#28a745',
                          fontWeight: '600'
                        }}>
                          <span>📝</span>
                          <span>Notas:</span>
                        </div>
                        <div style={{ paddingLeft: '20px', color: '#495057' }}>
                          {orden.notas}
                        </div>
                      </div>
                    )}

                    {/* Footer con total y acciones */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #e6f5e6',
                      paddingTop: '15px',
                      flexWrap: 'wrap',
                      gap: '10px'
                    }}>
                      <div style={{ 
                        backgroundColor: '#e8f5e8',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        color: '#212529'
                      }}>
                        Total: <span style={{ color: '#28a745' }}>${formatearNumero(orden.total)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => editarOrden(orden)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#ffc107',
                            color: 'black',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: getResponsiveSize('12px', '14px'),
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <span>✏️</span>
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => enviarACobrar(orden.id)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: getResponsiveSize('12px', '14px'),
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <span>💳</span>
                          <span>Enviar a Cobrar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return renderSeccion();
    }
  };

  // Componente de tarjeta de producto optimizado para móvil
  const ProductoCard = ({ producto, agregarProductoOrden, formatearNumero, isMobile, ordenActual }) => {
    const [agregando, setAgregando] = useState(false);
    
    // Verificar si el producto está en la orden actual
    const productoEnOrden = ordenActual?.productos?.find(p => p.id === producto.id);
    const cantidadEnOrden = productoEnOrden ? productoEnOrden.cantidadOrden : 0;
    const estaEnOrden = cantidadEnOrden > 0;
    
    // Debug: Log para verificar si se está detectando el producto en la orden
    useEffect(() => {
      if (estaEnOrden) {
        console.log(`Producto ${producto.nombre} está en orden con cantidad: ${cantidadEnOrden}`);
      }
    }, [estaEnOrden, cantidadEnOrden, producto.nombre]);
    
    const handleAgregarProducto = () => {
      if (isMobile()) {
        setAgregando(true);
        setTimeout(() => setAgregando(false), 300);
      }
      agregarProductoOrden(producto);
    };

    return (
      <div 
        key={producto.id} 
        style={{ 
          border: estaEnOrden ? '2px solid #007bff' : '1px solid #dee2e6',
          borderRadius: isMobile() ? '12px' : '6px', // Reducido en desktop
          padding: isMobile() ? '12px' : '8px', // Reducido en desktop
          backgroundColor: agregando ? '#e8f5e8' : (estaEnOrden ? '#f0f8ff' : '#fff'),
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: agregando ? 'scale(0.98)' : 'scale(1)',
          boxShadow: agregando ? '0 2px 8px rgba(40, 167, 69, 0.3)' : (estaEnOrden ? '0 2px 8px rgba(0, 123, 255, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'),
          display: isMobile() ? 'flex' : 'block',
          flexDirection: isMobile() ? 'row' : 'column',
          alignItems: isMobile() ? 'center' : 'initial',
          gap: isMobile() ? '12px' : '0',
          minHeight: isMobile() ? '80px' : 'auto',
          position: 'relative',
          maxHeight: isMobile() ? 'auto' : '220px' // Altura máxima en desktop para mantener uniformidad
        }}
        onMouseEnter={(e) => {
          if (!isMobile()) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = estaEnOrden ? '0 4px 12px rgba(0, 123, 255, 0.3)' : '0 4px 8px rgba(0,0,0,0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile()) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = estaEnOrden ? '0 2px 8px rgba(0, 123, 255, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)';
          }
        }}
      >
        {/* Indicador de cantidad en orden */}
        {estaEnOrden && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: '#007bff',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 1
          }}>
            {cantidadEnOrden}
          </div>
        )}
        
        {/* Imagen del producto */}
        {producto.imagenURL && (
          <img 
            src={producto.imagenURL} 
            alt={producto.nombre}
            style={{ 
              width: isMobile() ? '60px' : '100%', 
              height: isMobile() ? '60px' : '60px', // Reducido de 80px a 60px
              objectFit: 'cover', 
              borderRadius: '6px',
              marginBottom: isMobile() ? '0' : '6px', // Reducido el margen
              flexShrink: 0
            }}
          />
        )}
        
        {/* Contenido principal */}
        <div style={{
          flex: isMobile() ? 1 : 'none',
          textAlign: isMobile() ? 'left' : 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: isMobile() ? '60px' : 'auto'
        }}>
          {/* Información del producto */}
          <div>
            <h4 style={{ 
              margin: '0 0 3px 0', // Reducido el margen
              fontSize: isMobile() ? '15px' : '13px', // Reducido en desktop
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile() ? 'normal' : 'nowrap',
              lineHeight: isMobile() ? '1.2' : '1.1', // Reducido line-height
              display: isMobile() ? '-webkit-box' : 'block',
              WebkitLineClamp: isMobile() ? 2 : 'none',
              WebkitBoxOrient: isMobile() ? 'vertical' : 'initial'
            }}>{producto.nombre}</h4>
            
            <div style={{
              display: 'flex',
              flexDirection: isMobile() ? 'row' : 'column',
              alignItems: isMobile() ? 'center' : 'initial',
              justifyContent: isMobile() ? 'space-between' : 'initial',
              gap: isMobile() ? '8px' : '2px'
            }}>
              <p style={{ 
                margin: '0', 
                fontSize: isMobile() ? '14px' : '12px', // Reducido en desktop
                color: '#007bff',
                fontWeight: 'bold'
              }}>
                ${formatearNumero(producto.precio)}
              </p>
              
              <p style={{ 
                margin: '0', 
                fontSize: isMobile() ? '12px' : '10px', // Reducido en desktop
                color: producto.cantidad !== null ? (producto.cantidad > 0 ? '#28a745' : '#dc3545') : '#6c757d',
                fontWeight: '500'
              }}>
                Stock: {producto.cantidad !== null ? formatearNumero(producto.cantidad) : 'No definido'}
              </p>
            </div>
            
            {/* Mostrar categoría */}
            {producto.categoria && !isMobile() && (
              <span style={{
                display: 'inline-block',
                padding: '2px 6px',
                backgroundColor: '#e9ecef',
                borderRadius: '12px',
                fontSize: '10px',
                marginTop: '4px'
              }}>
                {producto.categoria}
              </span>
            )}
          </div>
        </div>
        
        {/* Botón de acción */}
        <button 
          onClick={handleAgregarProducto}
          disabled={producto.cantidad !== null && producto.cantidad <= 0}
          style={{
            width: isMobile() ? '70px' : '100%',
            height: isMobile() ? '35px' : '28px', // Reducido en desktop
            padding: isMobile() ? '4px 8px' : '4px', // Reducido padding en desktop
            backgroundColor: (producto.cantidad === null || producto.cantidad > 0) ? '#28a745' : '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px', // Reducido border-radius
            cursor: (producto.cantidad === null || producto.cantidad > 0) ? 'pointer' : 'not-allowed',
            fontSize: isMobile() ? '11px' : '11px', // Reducido en desktop
            fontWeight: 'bold',
            marginTop: isMobile() ? '0' : '6px', // Reducido margen
            transition: 'background-color 0.2s ease',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isMobile() ? ((producto.cantidad === null || producto.cantidad > 0) ? '+ Agregar' : 'Sin Stock') : ((producto.cantidad === null || producto.cantidad > 0) ? 'Agregar' : 'Sin Stock')}
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Toast notifications */}
      {toast.visible && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast} 
          duration={3000} 
        />
      )}

      <div 
        className="dashboard-container fade-in"
        style={{ 
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          fontSize: isMobile() ? '14px' : '16px',
          backgroundColor: '#f8f9fa',
          color: '#333',
          overflow: 'hidden', // Evitar doble scroll
          touchAction: 'pan-y' // Permitir scroll vertical en móvil
        }}
      >
        {/* Header móvil simplificado */}
        {isMobile() && (
          <div 
            style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1003, // Aumentado para mantener el header siempre visible
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              minHeight: '48px'
            }}
          >
            <button
              onClick={() => {
                if (!mostrarPanelOrden) {
                  setMenuMovilAbierto(!menuMovilAbierto);
                }
              }}
              disabled={mostrarPanelOrden}
              style={{
                padding: '8px',
                backgroundColor: mostrarPanelOrden ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px',
                fontSize: '16px',
                color: mostrarPanelOrden ? 'rgba(255,255,255,0.5)' : 'white',
                cursor: mostrarPanelOrden ? 'not-allowed' : 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              {menuMovilAbierto ? '✕' : '☰'}
            </button>
            
            <h1 style={{
              margin: 0,
              fontSize: '16px',
              color: 'white',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🍽️ Mesero
            </h1>
            
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <img
                src={userProfile.imagenURL || DEFAULT_PROFILE_IMAGE}
                alt="Perfil"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  objectFit: 'cover'
                }}
              />
              <div style={{
                color: 'white',
                fontSize: '12px'
              }}>
                <div style={{ fontWeight: 'bold' }}>{userProfile.nombre}</div>
                <div style={{ opacity: 0.9 }}>{userProfile.sucursal}</div>
              </div>
            </div>
          </div>
        )}

        {/* Menú móvil desplegable */}
        {isMobile() && menuMovilAbierto && !mostrarPanelOrden && (
          <>
            {/* Overlay para cerrar menú */}
            <div
              onClick={() => setMenuMovilAbierto(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 997 // Reducido para que esté por debajo del panel de orden
              }}
            />
            
            <div 
              style={{
                position: 'fixed',
                top: '60px',
                left: 0,
                width: '250px',
                height: 'calc(100vh - 60px)',
                backgroundColor: '#ffffff',
                borderRight: '1px solid #dee2e6',
                boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
                zIndex: 998, // Reducido para que esté por debajo del panel de orden
                overflowY: 'auto',
                padding: '20px'
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  margin: '0 0 15px 0',
                  fontSize: '16px',
                  color: '#333',
                  borderBottom: '1px solid #dee2e6',
                  paddingBottom: '8px'
                }}>
                  📋 Navegación
                </h3>
                
                {[
                  { id: 'panel', label: 'Panel Principal', icon: '📊' },
                  { id: 'tomar_orden', label: 'Tomar Orden', icon: '🍽️' },
                  { id: 'ordenes_proceso', label: 'En Proceso', icon: '⏳' },
                  { id: 'ordenes_cobrar', label: 'Para Cobrar', icon: '💰' }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSeccionActiva(item.id);
                      setMenuMovilAbierto(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      margin: '4px 0',
                      backgroundColor: seccionActiva === item.id ? '#007bff' : 'transparent',
                      color: seccionActiva === item.id ? 'white' : '#333',
                      border: seccionActiva === item.id ? 'none' : '1px solid #dee2e6',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span style={{ minWidth: '20px', textAlign: 'center' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              
              <div style={{
                borderTop: '1px solid #dee2e6',
                paddingTop: '15px',
                marginTop: '20px'
              }}>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  🚪 Cerrar Sesión
                </button>
              </div>
            </div>
          </>
        )}

        {/* Sidebar - Solo visible en desktop */}
        {!isMobile() && (
          <div style={{
            width: '250px',
            height: '100vh',
            background: '#ffffff',
            borderRight: '1px solid #dee2e6',
            position: 'fixed',
            left: '0',
            top: 0,
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none'
          }}>
          
          {/* Perfil del mesero mejorado */}
          <div style={{
            padding: '16px',
            backgroundColor: 'transparent',
            borderBottom: '1px solid rgba(222, 226, 230, 0.5)'
          }}>
            <div style={{
              padding: '14px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid rgba(222, 226, 230, 0.3)'
            }}>
              <div style={{ 
                width: '72px',
                height: '72px',
                margin: '0 auto 12px auto',
                position: 'relative'
              }}>
                <img 
                  src={userProfile.imagenURL}
                  alt={userProfile.nombre}
                  onError={handleImageError}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #007bff',
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)'
                  }}
                />
                {/* Status indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}></div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  margin: '0',
                  fontSize: '16px',
                  color: '#333',
                  fontWeight: '600'
                }}>
                  {userProfile.nombre}
                </h3>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: '#007bff',
                  color: 'white',
                  borderRadius: '16px',
                  fontSize: '12px',
                  marginTop: '6px',
                  fontWeight: '600',
                  boxShadow: '0 2px 6px rgba(0, 123, 255, 0.3)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}>
                  {userProfile.rol}
                </span>
                {userProfile.sucursal && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(108, 117, 125, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span>📍</span>
                    <span>{userProfile.sucursal}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navegación con tema café */}
          <nav style={{
            padding: '20px',
            flex: 1,
            overflowY: 'auto'
          }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('panel');
                    if (isMobile()) setMenuMovilAbierto(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: seccionActiva === 'panel' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center' }}>📊</span>
                  <span>Panel Principal</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('tomar_orden');
                    if (isMobile()) setMenuMovilAbierto(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: seccionActiva === 'tomar_orden' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center' }}>🍽️</span>
                  <span>Tomar Orden</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('ordenes_proceso');
                    if (isMobile()) setMenuMovilAbierto(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: seccionActiva === 'ordenes_proceso' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ minWidth: '20px', textAlign: 'center' }}>⏳</span>
                    <span>En Proceso</span>
                  </div>
                  {ordenesEnProceso.length > 0 && (
                    <span style={{
                      backgroundColor: 'white',
                      color: '#6c757d',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {ordenesEnProceso.length}
                    </span>
                  )}
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('ordenes_cobrar');
                    if (isMobile()) setMenuMovilAbierto(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: seccionActiva === 'ordenes_cobrar' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ minWidth: '20px', textAlign: 'center' }}>💰</span>
                    <span>Para Cobrar</span>
                  </div>
                  {ordenesParaCobrar.length > 0 && (
                    <span style={{
                      backgroundColor: 'white',
                      color: '#6c757d',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {ordenesParaCobrar.length}
                    </span>
                  )}
                </button>
              </li>
            </ul>
          </nav>

          {/* Botón de cerrar sesión con tema café */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
            >
              <span>🚪</span>
              Cerrar Sesión
            </button>
          </div>
        </div>
        )}

        {/* Main Content - Responsive positioning */}
        <div style={{ 
          position: 'absolute',
          left: isMobile() ? '0' : '250px',
          right: '0',
          top: '0',
          bottom: '0',
          padding: isMobile() ? '10px' : '20px',
          paddingTop: isMobile() ? '50px' : '20px',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch', // Scroll táctil mejorado
          zIndex: mostrarPanelOrden ? 999 : 1 // Menor z-index cuando el panel está abierto
        }}>
          {renderSeccion()}
        </div>    </div>
    </>
  );
}

export default DashboardMesero;
